import React, { useState } from 'react';
import { CustomerDetails } from '../../../components/iqbd/CustomerDetails';
import useIQBDForm from '../../../components/iqbd/useIQBDForm';
import {
  FORM_TYPE,
  FORM_TYPE_ACTION_BUTTON_TYPES,
} from '../../../components/iqbd/Form';
import useContentMgmt, {
  PageTextContentType,
} from '../../../hooks/useContentMgmt';
import colors from '../../../themes/main-colors';

import {
  Box,
  Button,
  Checkbox,
  FormControlLabel,
  FormHelperText,
  Grid,
  List,
  ListItem,
  Theme,
  Typography,
  useTheme,
} from '@material-ui/core';
import makeStyles from '@material-ui/styles/makeStyles';
import CurrencyTextField from '../../../components/currency-text-field';
import { SelectDropdown } from '../../../components/iqbd/Form/SelectDropdown';
import { Info } from '@material-ui/icons';
import { RadioOption } from '../../../components/iqbd/Form/RadioOption';
import { C2MPreferredLanguages } from '../../../util/languageUtil';
import { navigate } from 'gatsby';
import ROUTES from '../../../routes';
import {
  IqbdEnrollIneligibilityReason,
  IqbdEnrollInput,
  IqbdOperations,
} from '../../../__generated__/pge-types';
import iqbdFormStateAdapter from '../../../components/iqbd/utils/iqbdFormStateAdapter';
import { IQBDModel } from '../../../components/iqbd/IQBDForm.rules';
import useWrapWithLoader from '../../../hooks/useWrapWithLoading';
import useUtil from '../../../components/need-more-time-to-pay/useUtil';
import IQBDHeaderCard, {
  IQBD_HEADER_CARD_TYPES,
} from '../../../components/iqbd/IQBDHeaderCard';

import TextField from '../../text-field';
import { ApplicantDetails } from '../ApplicantDetails';
import { HouseholdIncomeGuidelines } from '../Form/HouseholdIncomeGuidelines';

const useStyles = makeStyles((theme: Theme) => ({
  applyingForBox: {
    border: `1px solid ${colors.lightGray}`,
    backgroundColor: colors.white,
    borderRadius: '16px',
    padding: '48px 0',
    width: '333px',
    textAlign: 'center',
    fontSize: '18px',
    cursor: 'pointer',
    fontFamily: 'Untitled-Sans',
    transition: 'all 0.2s',
  },
  selectedApplyingForBox: {
    background: colors.riverBlue,
    boxShadow: '0px 3px 6px #0000004D',
    fontWeight: 500,
  },
  miniApplyingForBox: {
    padding: '12px',
  },
  formSection: {
    background: `${colors.white} 0% 0% no-repeat padding-box`,
    borderRadius: '5px',
    padding: '30px 20px',
    margin: '10px 0',
  },
  sectionHeading: {
    fontFamily: 'Forma-DJR-Display',
    fontWeight: 'bold',
    fontSize: '24px',
    lineHeight: '28px',
    letterSpacing: '0.48px',
    color: colors.lightCyan,
    marginBottom: '15px',
  },
  sectionInfo: {
    fontFamily: 'Untitled-Sans',
    fontSize: '16px',
    lineHeight: '20px',
    color: colors.lightCyan,
    display: 'block',
    margin: '5px 0',
    '&:last-child': {
      marginBottom: 0,
    },
  },
  highlighted: {
    textDecoration: 'underline',
    fontWeight: 'bold',
    color: colors.sparkBlue,
    cursor: 'pointer',
    fontSize: 'inherit',
    lineHeight: 'inherit',
  },
  button: {
    borderRadius: '5px',
    padding: '13px 57px',
    fontWeight: 'bold',
    fontSize: '18px',
    fontFamily: 'Untitled-Sans',
    display: 'block',
    '&:hover': {
      boxShadow: 'none',
    },
  },
  textfield: {
    margin: theme.spacing(0.25, 0, 0, 0),
    '& label': {
      whiteSpace: 'nowrap',
      overflow: 'hidden',
      textOverflow: 'ellipsis',
      width: '90%',
      fontSize: '16px',
      transform: 'translate(14px, 16px) scale(1)',
      '&.MuiInputLabel-shrink': {
        overflow: 'unset',
      },
    },
    '& input': {
      height: '48px',
      fontSize: '16px',
      padding: '0 14px',
    },
  },
  listItem: {
    display: 'list-item',
    padding: 0,
    fontSize: '16px',
    lineHeight: '20px',
    fontFamily: 'Untitled-Sans',
    margin: '8px 0',
  },
}));

interface IQBDPublicFormProps {
  content: PageTextContentType;
  richText: (text: string | undefined) => React.ReactNode;
}

const IQBDPublicForm: React.FC<IQBDPublicFormProps> = ({
  content,
  richText,
}) => {
  const [formTypeActionButtonTypes, setFormTypeActionButtonTypes] = useState<
    FORM_TYPE_ACTION_BUTTON_TYPES[]
  >([]);

  const [income, setIncome] = useState<any>(null);
  const [incomePeriod, setIncomePeriod] = useState<string>('Monthly');
  const [showForm, setShowForm] = useState<boolean>(true);
  const [headercardData, setHeadercardData] = useState<any>(null);
  const [showEmailPhoneError, setShowEmailPhoneError] = useState<any>(null);

  const [premiseId, setPremiseId] = useState<string>('');
  const [ismultiPremiseId, setIsmultiPremiseId] = useState<boolean>(false);

  const [needHelpToCalculateIncome, setNeedHelpToCalculateIncome] = useState<
    boolean
  >(false);
  const { wrapWithLoader } = useWrapWithLoader();
  const { setErrorNotification } = useUtil();

  const { form, submitForm } = useIQBDForm(content, FORM_TYPE.Public);
  const handleSubmit = wrapWithLoader(async (data: IQBDModel) => {
    if (!form.values.applicantPhone && !form.values.applicantEmail) {
      setShowEmailPhoneError(true);
      return;
    } else {
      if (ismultiPremiseId) {
        setShowForm(false);

        setHeadercardData({
          type: IQBD_HEADER_CARD_TYPES.Ineligible,
          reason: IqbdEnrollIneligibilityReason.MultiPremise,
        });

        window.scrollTo({
          top: 0,
          behavior: 'smooth',
        });
      } else {
        try {
          const enrollAction: IqbdOperations = IqbdOperations.Enroll;
          const formData: IqbdEnrollInput = iqbdFormStateAdapter.fromFormState(
            content,
            data,
            data.accountNumber || '',
            premiseId,
          );

          const result = await submitForm({
            ...formData,
            action: enrollAction,
          });

          if (result?.data?.IQBDEnroll.isEnrolled) {
            setShowForm(false);
            setHeadercardData({
              type: IQBD_HEADER_CARD_TYPES.Success,
              discount: result.data.IQBDEnroll.discountPercentage?.replace(
                'PCT',
                '%',
              ),
            });
          } else {
            setShowForm(false);
            setHeadercardData({
              type: IQBD_HEADER_CARD_TYPES.Ineligible,
              reason: result?.data?.IQBDEnroll.reason,
            });
          }
          window.scrollTo({
            top: 0,
            behavior: 'smooth',
          });
        } catch (e) {
          console.log(e);
          setErrorNotification(true);
        }
      }
    }
  });

  const [
    showHouseholdIncomeGuidelines,
    setShowHouseholdIncomeGuidelines,
  ] = useState<boolean>(false);

  const theme = useTheme();
  const classes = useStyles();

  return (
    <form onSubmit={form.submit(values => handleSubmit(values))}>
      <Grid
        container
        css={{
          borderRadius: '4px',
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
        }}
      >
        {' '}
        {showForm && (
          <Grid
            item
            css={{
              boxShadow: '0px 3px 6px #0000004D',
              borderRadius: '4px',
              backgroundColor: colors.riverBlue,
              padding: '20px !important',
              display: 'flex',
              flexDirection: 'column',
            }}
          >
            {' '}
            <Box>
              <Typography
                css={{
                  font: 'Untitled-Sans',
                  color: colors.lightCyan,
                  fontSize: '18px',
                  lineHeight: '20px',
                  marginBottom: '20px',
                }}
              >
                {richText(content.get('FORM_DESCRIPTION'))}
              </Typography>
            </Box>
            <Box className={classes.formSection}>
              <ApplicantDetails
                form={form}
                showEmailPhoneError={showEmailPhoneError}
              />
              <CustomerDetails
                form={form}
                setPremiseId={setPremiseId}
                setIsmultiPremiseId={setIsmultiPremiseId}
              />
            </Box>
            <Box className={classes.formSection}>
              <Typography className={classes.sectionHeading}>
                {content.get('HOUSEHOLD_INCOME_INFO_HEADING')}
              </Typography>
              <Typography className={classes.sectionInfo} css={{ margin: 0 }}>
                {content.get('HOUSEHOLD_INCOME_DESC')}
              </Typography>
              <Typography
                className={classes.sectionInfo}
                css={{ margin: '16px 0 30px 0' }}
              >
                {content.get('HOUSEHOLD_INCOME_UNSURE_TEXT_1')}{' '}
                <Typography
                  component="span"
                  className={classes.highlighted}
                  onClick={() => setShowHouseholdIncomeGuidelines(true)}
                >
                  {content.get('HOUSEHOLD_INCOME_UNSURE_TEXT_2')}
                </Typography>
                .
              </Typography>

              <Box mb={'20px'}>
                <SelectDropdown
                  title={content.get('HOUSEHOLD_SIZE_TITLE')}
                  selectedValue={
                    form.values.houseHoldSize
                      ? form.values.houseHoldSize.toString()
                      : ''
                  }
                  placeholder={content.get('PLEASE_SELECT')}
                  options={Array.from({ length: 15 }, (__, i) => i + 1)}
                  onChange={value => {
                    void form.setValue('houseHoldSize', value);
                  }}
                />
                {form.errors.houseHoldSize && (
                  <FormHelperText error={true}>
                    {form.errors.houseHoldSize}
                  </FormHelperText>
                )}
              </Box>

              <Box
                mb={'10px'}
                css={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: '5px',
                }}
              >
                <Typography
                  css={{
                    fontSize: '16px',
                    lineHeight: '20px',
                    color: colors.lightCyan,
                    display: 'block',
                  }}
                >
                  {richText(content.get('NEED_HELP_CALCULATING_INCOME'))}
                </Typography>
                <Info
                  css={{ cursor: 'pointer' }}
                  onClick={() =>
                    setNeedHelpToCalculateIncome(!needHelpToCalculateIncome)
                  }
                />
              </Box>

              {needHelpToCalculateIncome && (
                <Box
                  css={{
                    background: colors.cloudGrey,
                    borderRadius: '5px',
                    padding: '30px 27px',
                    marginBottom: '16px',
                  }}
                >
                  <Typography
                    className={classes.sectionInfo}
                    css={{ marginBottom: '20px' }}
                  >
                    {content.get('CALCULATE_INCOME_TEXT_1')}
                  </Typography>

                  <Typography
                    className={classes.sectionInfo}
                    css={{ margin: '20px 0 30px 0' }}
                  >
                    {content.get('CALCULATE_INCOME_TEXT_6')}
                  </Typography>

                  <Typography className={classes.sectionInfo}>
                    {content.get('CALCULATE_INCOME_TEXT_7')}{' '}
                    <Typography
                      component="span"
                      className={classes.highlighted}
                    >
                      {content.get('ASSISTANCE_PHONE_NUMBER')}
                    </Typography>
                    .
                  </Typography>
                </Box>
              )}

              <Grid
                xs={12}
                container
                css={{
                  marginBottom: '30px',
                }}
              >
                <Typography className={classes.sectionInfo}>
                  {content.get('AVG_GROSS_ANNUAL_INCOME')}
                </Typography>

                <Grid container item spacing={2} css={{ marginTop: '8px' }}>
                  <Grid item xs={5} md={3}>
                    <CurrencyTextField
                      css={{
                        margin: theme.spacing(0, 0, 0, 0),
                        '& label': {
                          whiteSpace: 'nowrap',
                          overflow: 'hidden',
                          textOverflow: 'ellipsis',
                          width: '90%',
                          fontSize: '16px',
                          transform: 'translate(14px, 16px) scale(1)',
                          '&.MuiInputLabel-shrink': {
                            overflow: 'unset',
                          },
                        },
                        '& input': {
                          height: '48px',
                          fontSize: '16px',
                          padding: '0 14px',
                        },
                      }}
                      label={content.get('INCOME')}
                      placeholder={'income'}
                      onChange={(e: any) => {
                        const inputVal = Number(e.target.value);
                        setIncome(inputVal);
                        let calculatedIncome = null;
                        if (incomePeriod === content.get('MONTHLY')) {
                          calculatedIncome = inputVal * 12;
                        } else if (incomePeriod === content.get('WEEKLY')) {
                          calculatedIncome = inputVal * 52;
                        } else if (incomePeriod === content.get('BIWEEKLY')) {
                          calculatedIncome = inputVal * 26;
                        } else {
                          calculatedIncome = inputVal;
                        }
                        void form.setValue(
                          'annualIncome',
                          calculatedIncome.toString(),
                        );
                      }}
                      error={false}
                    />
                  </Grid>
                  <Grid
                    item
                    xs={2}
                    md={1}
                    css={{
                      display: 'flex',
                      justifyContent: 'center',
                      alignItems: 'center',
                    }}
                  >
                    <Typography className={classes.sectionInfo}>
                      {content.get('PER')}
                    </Typography>
                  </Grid>
                  <Grid item xs={5} md={2}>
                    <SelectDropdown
                      isWidth={false}
                      selectedValue={incomePeriod}
                      placeholder={content.get('PLEASE_SELECT')}
                      options={[
                        content.get('WEEKLY'),
                        content.get('BIWEEKLY'),
                        content.get('MONTHLY'),
                        content.get('ANNUAL'),
                      ]}
                      onChange={value => {
                        setIncomePeriod(value);
                        let calculatedIncome = null;
                        if (incomePeriod !== value) {
                          if (value === content.get('MONTHLY')) {
                            calculatedIncome = income * 12;
                          } else if (value === content.get('WEEKLY')) {
                            calculatedIncome = income * 52;
                          } else if (value === content.get('BIWEEKLY')) {
                            calculatedIncome = income * 26;
                          } else {
                            calculatedIncome = income;
                          }
                        }
                        void form.setValue('annualIncome', calculatedIncome);
                      }}
                    />
                  </Grid>
                </Grid>

                {form.errors.annualIncome && (
                  <FormHelperText error={true}>
                    {form.errors.annualIncome}
                  </FormHelperText>
                )}

                {incomePeriod && (
                  <Typography style={{ marginTop: '16px', fontWeight: 500 }}>
                    {content.get('CALCULATED_INCOME')} = $
                    {(Number(form.values.annualIncome) || 0).toLocaleString()}
                  </Typography>
                )}
              </Grid>

              <Box
                mb={'30px'}
                css={
                  form.errors.confirmAnnualIncome
                    ? {
                        '&>div>p:first-child': {
                          color: colors.errorRed,
                        },
                      }
                    : {}
                }
              >
                <FormControlLabel
                  control={
                    <Checkbox
                      color={
                        form.values.confirmAnnualIncome ? 'primary' : 'default'
                      }
                      size={'medium'}
                      css={{ padding: '0' }}
                    />
                  }
                  checked={Boolean(form.values.confirmAnnualIncome)}
                  onChange={() =>
                    form.setValue(
                      'confirmAnnualIncome',
                      !form.values.confirmAnnualIncome,
                    )
                  }
                  label={
                    <Typography
                      css={{
                        fontSize: '16px',
                        marginLeft: '8px',
                        color: form.errors.confirmAnnualIncome
                          ? colors.errorRed
                          : colors.lightCyan,
                      }}
                    >
                      {content.get('CONFIRM_ANNUAL_INCOME')}
                    </Typography>
                  }
                  css={{
                    display: 'flex',
                    alignItems: 'center',
                    marginLeft: 0,
                  }}
                />
              </Box>

              <Box mb={'30px'}>
                <RadioOption
                  title={content.get('FIXED_INCOME')!}
                  yesOptionTitle={content.get('REUSABLE_YES')}
                  noOptionTitle={content.get('REUSABLE_NO')}
                  selectedValue={form.values.fixedIncome}
                  onChange={value => {
                    void form.setValue('fixedIncome', value);
                  }}
                />
                {form.errors.fixedIncome && (
                  <FormHelperText error={true}>
                    {form.errors.fixedIncome}
                  </FormHelperText>
                )}
              </Box>

              <Box>
                <Box
                  mb={
                    form.values.ethinicity === content.get('OTHER')
                      ? '0px'
                      : '40px'
                  }
                >
                  <SelectDropdown
                    title={content.get('ETHNICITY_TEXT')}
                    selectedValue={form.values.ethinicity}
                    placeholder={content.get('PLEASE_SELECT')}
                    options={[
                      content.get('ETHNICITY_1'),
                      content.get('ETHNICITY_2'),
                      content.get('ETHNICITY_3'),
                      content.get('ETHNICITY_4'),
                      content.get('ETHNICITY_5'),
                      content.get('ETHNICITY_6'),
                      content.get('ETHNICITY_7'),
                      content.get('ETHNICITY_8'),
                      content.get('ETHNICITY_9'),
                      content.get('OTHER'),
                      content.get('PREFER_NOT_TO_RESPOND'),
                    ]}
                    onChange={value => {
                      void form.setValue('ethinicity', value);
                    }}
                  />
                  {form.errors.ethinicity && (
                    <FormHelperText error={true}>
                      {form.errors.ethinicity}
                    </FormHelperText>
                  )}
                </Box>
              </Box>

              {form.values.ethinicity === 'Other' && (
                <Box mb={'40px'}>
                  <TextField
                    name={content.get('OTHER_ETHNICITY')}
                    label={content.get('OTHER_ETHNICITY')}
                    value={form.values.otherEthnicity}
                    onChange={(e: any) => {
                      void form.setValue('otherEthnicity', e.target.value);
                    }}
                    css={{
                      margin: theme.spacing(2, 0, 0, 0),
                      width: '315px !important',
                      '& label': {
                        whiteSpace: 'nowrap',
                        overflow: 'hidden',
                        textOverflow: 'ellipsis',
                        width: '90%',
                        fontSize: '16px',
                        transform: 'translate(14px, 16px) scale(1)',
                        '&.MuiInputLabel-shrink': {
                          overflow: 'unset',
                        },
                      },
                      '& input': {
                        height: '48px',
                        fontSize: '16px',
                        padding: '0 14px',
                      },
                    }}
                  />
                  {form.errors.otherEthnicity && (
                    <FormHelperText error={true}>
                      {form.errors.otherEthnicity}
                    </FormHelperText>
                  )}
                </Box>
              )}
              <Box>
                <Box mb={'40px'}>
                  <SelectDropdown
                    title={content.get('PREFERRED_CONTACT_LANGUAGE')}
                    placeholder={content.get('PLEASE_SELECT')}
                    selectedValue={form.values.prefLanguage}
                    options={Object.keys(C2MPreferredLanguages)}
                    onChange={value => {
                      void form.setValue('prefLanguage', value);
                    }}
                  />
                  {form.errors.prefLanguage && (
                    <FormHelperText error={true}>
                      {form.errors.prefLanguage}
                    </FormHelperText>
                  )}
                </Box>
              </Box>
            </Box>
            <Box className={classes.formSection}>
              <Typography className={classes.sectionHeading}>
                {content.get('DECLARATION')}
              </Typography>
              <List
                component="ol"
                className={classes.sectionInfo}
                css={{
                  padding: '20px 20px 0 20px',
                  listStyleType: 'decimal',
                  listStyle: 'auto',
                }}
              >
                <ListItem component="li" className={classes.listItem}>
                  {content.get('DECLARATION_TEXT_1')}
                </ListItem>
                <ListItem component="li" className={classes.listItem}>
                  {content.get('DECLARATION_TEXT_2')}
                </ListItem>
                <ListItem component="li" className={classes.listItem}>
                  {content.get('DECLARATION_TEXT_3')}
                </ListItem>
              </List>
              <Typography
                className={classes.sectionInfo}
                css={{ margin: '20px 0' }}
              >
                {richText(content.get('ELECTRONIC_SIGNATURE_DECLARATION'))}
              </Typography>
              <Typography
                css={{
                  fontFamily: 'Forma-DJR-Display',
                  fontSize: '18px',
                  lineHeight: '28px',
                  fontWeight: 'bold',
                  letterSpacing: '0.36px',
                  color: colors.lightCyan,
                }}
              >
                {content.get('ELECTRONIC_SIGNATURE')}
              </Typography>

              <TextField
                name={content.get('ENTER_FULL_NAME')}
                label={content.get('ENTER_FULL_NAME')}
                value={form.values.signature}
                onChange={(e: any) => {
                  void form.setValue('signature', e.target.value);
                }}
                css={{
                  margin: theme.spacing(2, 0, 0, 0),
                  width: '315px !important',
                  '& label': {
                    whiteSpace: 'nowrap',
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                    width: '90%',
                    fontSize: '16px',
                    transform: 'translate(14px, 16px) scale(1)',
                    '&.MuiInputLabel-shrink': {
                      overflow: 'unset',
                    },
                  },
                  '& input': {
                    height: '48px',
                    fontSize: '16px',
                    padding: '0 14px',
                  },
                }}
              />
              {form.errors.signature && (
                <FormHelperText error={true}>
                  {form.errors.signature}
                </FormHelperText>
              )}
            </Box>
          </Grid>
        )}
        {headercardData && !showForm && (
          <IQBDHeaderCard
            content={content}
            {...headercardData}
            formTypeActionButtonTypes={formTypeActionButtonTypes}
            handleUpdate={() => {
              setShowForm(true);
            }}
            handleUnEnroll={() => null}
            onClickDone={() => {
              setHeadercardData(null);
              setShowForm(true);
            }}
            selectedTab={FORM_TYPE.Public}
          />
        )}
      </Grid>
      {headercardData && !showForm && (
        <Box
          css={{
            display: 'flex',
            gap: '32px',
            alignItems: 'center',
            justifyContent: 'flex-end',
            marginTop: '24px',
          }}
        >
          <Button
            variant="outlined"
            color="primary"
            css={{
              backgroundColor: colors.buttonHoverPrimary,
              border: `1px solid ${colors.buttonHoverPrimary}`,
              color: colors.white,
              '&:hover': {
                backgroundColor: colors.buttonHoverPrimary,
                borderColor: colors.buttonHoverPrimary,
                color: colors.white,
              },
            }}
            className={classes.button}
            onClick={() => navigate(ROUTES.SIGN_IN)}
          >
            {content.get('DONE')}
          </Button>
        </Box>
      )}
      {showForm && (
        <Box
          css={{
            display: 'flex',
            gap: '32px',
            alignItems: 'center',
            justifyContent: 'flex-end',
            marginTop: '24px',
          }}
        >
          <Button
            variant="outlined"
            color="primary"
            className={classes.button}
            onClick={() => navigate(ROUTES.ACCOUNT)}
          >
            {content.get('REUSABLE_CANCEL')}
          </Button>
          <Button
            type={'submit'}
            className={classes.button}
            css={{
              backgroundColor: colors.buttonHoverPrimary,
              border: `1px solid ${colors.buttonHoverPrimary}`,
              color: colors.white,
              '&:hover': {
                backgroundColor: colors.buttonHoverPrimary,
                borderColor: colors.buttonHoverPrimary,
                color: colors.white,
              },
            }}
          >
            {content.get('SUBMIT')}
          </Button>
        </Box>
      )}
      <HouseholdIncomeGuidelines
        isOpen={showHouseholdIncomeGuidelines}
        content={content}
        handleClose={() => setShowHouseholdIncomeGuidelines(false)}
      />
    </form>
  );
};

export default IQBDPublicForm;
